/**
 * Service Worker Health Check and Auto-Recovery
 */

export interface HealthCheckResult {
  isHealthy: boolean;
  isRegistered: boolean;
  isActive: boolean;
  hasSubscription: boolean;
  issues: string[];
  lastCheck: Date;
}

class ServiceWorkerHealthCheck {
  private static instance: ServiceWorkerHealthCheck;
  private healthCheckInterval: NodeJS.Timeout | null = null;
  private readonly CHECK_INTERVAL = 5 * 60 * 1000;
  private lastHealthStatus: HealthCheckResult | null = null;
  private onHealthStatusChange?: (status: HealthCheckResult) => void;

  private constructor() {}

  public static getInstance(): ServiceWorkerHealthCheck {
    if (!ServiceWorkerHealthCheck.instance) {
      ServiceWorkerHealthCheck.instance = new ServiceWorkerHealthCheck();
    }
    return ServiceWorkerHealthCheck.instance;
  }

  public startMonitoring(onStatusChange?: (status: HealthCheckResult) => void): void {
    this.onHealthStatusChange = onStatusChange;
    this.performHealthCheck();

    if (this.healthCheckInterval) {
      clearInterval(this.healthCheckInterval);
    }

    this.healthCheckInterval = setInterval(() => {
      this.performHealthCheck();
    }, this.CHECK_INTERVAL);

    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        this.performHealthCheck();
      }
    });
  }

  public stopMonitoring(): void {
    if (this.healthCheckInterval) {
      clearInterval(this.healthCheckInterval);
      this.healthCheckInterval = null;
    }
  }

  public async performHealthCheck(): Promise<HealthCheckResult> {
    const issues: string[] = [];
    let isRegistered = false;
    let isActive = false;
    let hasSubscription = false;

    try {
      if (!('serviceWorker' in navigator)) {
        issues.push('Service Worker non supporté');
        return this.createHealthResult(false, false, false, false, issues);
      }

      const registration = await navigator.serviceWorker.getRegistration('/');

      if (!registration) {
        issues.push('Service Worker non enregistré');
        await this.recoverServiceWorker();
        return this.performHealthCheck();
      }

      isRegistered = true;

      if (!registration.active) {
        if (registration.installing) {
          issues.push('SW en cours d\'installation');
        } else if (registration.waiting) {
          issues.push('SW en attente d\'activation');
          registration.waiting.postMessage({ type: 'SKIP_WAITING' });
        } else {
          issues.push('SW non actif');
          await this.recoverServiceWorker();
          return this.performHealthCheck();
        }
      } else {
        isActive = true;
      }

      if (registration.pushManager) {
        const subscription = await registration.pushManager.getSubscription();
        if (subscription) {
          hasSubscription = true;
          if (subscription.expirationTime && subscription.expirationTime < Date.now()) {
            issues.push('Abonnement push expiré');
            hasSubscription = false;
          }
        }
      }

      if (Notification.permission !== 'granted') {
        issues.push('Permissions non accordées');
      }

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      issues.push(`Erreur: ${errorMessage}`);
    }

    const isHealthy = isRegistered && isActive && hasSubscription && issues.length === 0;
    const result = this.createHealthResult(isHealthy, isRegistered, isActive, hasSubscription, issues);

    if (this.hasStatusChanged(result)) {
      if (!isHealthy) {
        console.warn('[SW-HEALTH] ⚠️ Issues:', issues);
      }
      if (this.onHealthStatusChange) {
        this.onHealthStatusChange(result);
      }
    }

    this.lastHealthStatus = result;
    return result;
  }

  private async recoverServiceWorker(): Promise<void> {
    try {
      await navigator.serviceWorker.register('/service-worker.js', {
        scope: '/',
        updateViaCache: 'none'
      });
      await navigator.serviceWorker.ready;
      await new Promise(resolve => setTimeout(resolve, 1000));
    } catch (error) {
      console.error('[SW-HEALTH] Recovery failed:', error);
      throw error;
    }
  }

  private hasStatusChanged(newStatus: HealthCheckResult): boolean {
    if (!this.lastHealthStatus) return true;
    return this.lastHealthStatus.isHealthy !== newStatus.isHealthy ||
           this.lastHealthStatus.hasSubscription !== newStatus.hasSubscription;
  }

  private createHealthResult(
    isHealthy: boolean,
    isRegistered: boolean,
    isActive: boolean,
    hasSubscription: boolean,
    issues: string[]
  ): HealthCheckResult {
    return { isHealthy, isRegistered, isActive, hasSubscription, issues, lastCheck: new Date() };
  }

  public getLastHealthStatus(): HealthCheckResult | null {
    return this.lastHealthStatus;
  }

  public async forceUpdate(): Promise<void> {
    const registration = await navigator.serviceWorker.getRegistration('/');
    if (registration) {
      await registration.update();
    }
  }
}

export default ServiceWorkerHealthCheck.getInstance();
